/**
 * TriggerDetector - Advanced trigger phrase detection with fuzzy matching
 * 
 * Consumes partial speech results and maintains a sliding window of normalized tokens.
 * Implements fuzzy matching for trigger phrases with configurable thresholds.
 */

export class TriggerDetector {
    constructor(options = {}) {
        this.options = {
            // Wake sequences - exact 2-token phrases only
            wakeSequences: options.wakeSequences || [['hey', 'chef']],
            
            // Sliding window settings
            windowMs: options.windowMs || 4000, // 4 seconds window
            maxTokens: options.maxTokens || 20, // maximum tokens to keep
            
            // Exact matching settings
            requireConsecutiveMatches: options.requireConsecutiveMatches !== false,
            consecutiveMatchCount: options.consecutiveMatchCount || 1, // Only need 1 match
            maxGapTokens: options.maxGapTokens || 0, // No gaps allowed
            
            // Negation detection
            negationWords: options.negationWords || ['not', "don't", "didn't", 'without', 'no', 'never'],
            negationWindow: options.negationWindow || 4, // tokens around candidate match
            
            // Refractory period
            refractoryPeriod: options.refractoryPeriod || 3000, // ms
            
            ...options
        };
        
        // State
        this.tokenWindow = [];
        this.lastTriggerTime = 0;
        this.consecutiveMatches = 0;
        this.lastMatchScore = 0;
        this.lastMatchedPhrase = '';
        
        // Callbacks
        this.onTriggerDetected = null;
        this.onTriggerRejected = null;
        
        console.log('🎯 TriggerDetector: Initialized with phrases:', this.options.triggerPhrases);
    }
    
    /**
     * Process a partial speech result
     * @param {Object} result - Partial result from speech engine
     */
    processPartialResult(result) {
        if (!result.transcript) return;
        
        // Normalize and tokenize the transcript
        const tokens = this.normalizeAndTokenize(result.transcript);
        
        // Add tokens to sliding window
        this.addTokensToWindow(tokens, result.timestamp || Date.now());
        
        // Check for trigger phrases
        this.checkForTriggers();
    }

    /**
     * Push normalized transcript for trigger detection
     * @param {string} normalizedTranscript - Normalized transcript
     */
    pushPartial(normalizedTranscript) {
        if (!normalizedTranscript) return;
        
        // Normalize and tokenize the transcript
        const tokens = this.normalizeAndTokenize(normalizedTranscript);
        
        // Add tokens to sliding window
        this.addTokensToWindow(tokens, Date.now());
        
        // Check for trigger phrases
        this.checkForTriggers();
    }
    
    /**
     * Normalize and tokenize text
     * @param {string} text - Input text
     * @returns {Array} Array of normalized tokens
     */
    normalizeAndTokenize(text) {
        return text
            .toLowerCase()
            .replace(/[^\w\s]/g, ' ') // Remove punctuation
            .replace(/\s+/g, ' ') // Normalize whitespace
            .trim()
            .split(' ')
            .filter(token => token.length > 0 && !this.isFillerWord(token))
            .map(token => this.normalizeToken(token)); // Apply token normalization
    }
    
    /**
     * Normalize individual tokens for better recognition
     * @param {string} token - Token to normalize
     * @returns {string} Normalized token
     */
    normalizeToken(token) {
        // Handle common mishearings
        const mishearingMap = {
            'chief': 'chef',
            'chef': 'chef',
            'hey': 'hey',
            'hi': 'hey',
            'ok': 'ok',
            'okay': 'ok'
        };
        
        return mishearingMap[token] || token;
    }
    
    /**
     * Check if a token is a filler word
     * @param {string} token - Token to check
     * @returns {boolean} True if filler word
     */
    isFillerWord(token) {
        const fillers = ['uh', 'um', 'er', 'ah', 'like', 'you know', 'so', 'well'];
        return fillers.includes(token);
    }
    
    /**
     * Add tokens to the sliding window
     * @param {Array} tokens - Tokens to add
     * @param {number} timestamp - Timestamp of the tokens
     */
    addTokensToWindow(tokens, timestamp) {
        // Add each token with timestamp
        tokens.forEach(token => {
            this.tokenWindow.push({
                token,
                timestamp,
                id: Date.now() + Math.random() // Unique ID
            });
        });
        
        // Trim window to max size
        if (this.tokenWindow.length > this.options.maxTokens) {
            this.tokenWindow = this.tokenWindow.slice(-this.options.maxTokens);
        }
        
        // Remove old tokens (older than window size)
        const cutoffTime = timestamp - (this.options.windowSize * 1000);
        this.tokenWindow = this.tokenWindow.filter(item => item.timestamp > cutoffTime);
    }
    
    /**
     * Check for exact sequence match in tokens
     * @param {Array} tokens - Array of tokens to search in
     * @param {Array} sequence - Array of tokens to match
     * @returns {boolean} True if exact sequence found
     */
    checkExactSequence(tokens, sequence) {
        if (sequence.length > tokens.length) {
            return false;
        }
        
        // Check for exact sequence match
        for (let i = 0; i <= tokens.length - sequence.length; i++) {
            let match = true;
            for (let j = 0; j < sequence.length; j++) {
                if (tokens[i + j] !== sequence[j]) {
                    match = false;
                    break;
                }
            }
            if (match) {
                return true;
            }
        }
        
        return false;
    }

    /**
     * Check for trigger phrases in the current window
     */
    checkForTriggers() {
        // Check if we're in refractory period
        const now = Date.now();
        if (now - this.lastTriggerTime < this.options.refractoryPeriod) {
            return;
        }
        
        // Get current tokens from window
        const tokens = this.tokenWindow.map(item => item.token);
        
        if (tokens.length < 2) {
            return; // Need at least 2 tokens for "hey chef"
        }
        
        // Check each wake sequence
        for (const sequence of this.options.wakeSequences) {
            if (this.checkExactSequence(tokens, sequence)) {
                const phrase = sequence.join(' ');
                const currentText = tokens.join(' ');
                
                // Check for negation
                if (this.hasNegation(currentText, phrase)) {
                    console.log(`🎯 TriggerDetector: Wake sequence "${phrase}" detected but negated`);
                    this.emitTriggerRejected(phrase, 1.0, 'negated');
                    continue;
                }
                
                // Check consecutive matches requirement
                if (this.options.requireConsecutiveMatches) {
                    if (this.lastMatchedPhrase === phrase) {
                        this.consecutiveMatches++;
                    } else {
                        this.consecutiveMatches = 1;
                    }
                    
                    if (this.consecutiveMatches < this.options.consecutiveMatchCount) {
                        console.log(`🎯 TriggerDetector: Wake sequence "${phrase}" detected but needs ${this.options.consecutiveMatchCount} consecutive matches (current: ${this.consecutiveMatches})`);
                        this.lastMatchedPhrase = phrase;
                        continue;
                    }
                }
                
                // Trigger detected!
                console.log(`🎯 TriggerDetector: Wake sequence "${phrase}" detected (consecutive: ${this.consecutiveMatches})`);
                this.emitTriggerDetected(phrase, 1.0, currentText);
                
                // Reset state
                this.lastTriggerTime = now;
                this.consecutiveMatches = 0;
                this.lastMatchScore = 0;
                this.lastMatchedPhrase = '';
                
                return; // Only trigger once per check
            }
        }
        
        // Reset consecutive matches if no match found
        this.consecutiveMatches = 0;
        this.lastMatchScore = 0;
        this.lastMatchedPhrase = '';
    }
    
    /**
     * Calculate string similarity using Levenshtein distance
     * @param {string} text1 - First text
     * @param {string} text2 - Second text
     * @returns {number} Similarity score (0-1)
     */
    calculateSimilarity(text1, text2) {
        const distance = this.levenshteinDistance(text1, text2);
        const maxLength = Math.max(text1.length, text2.length);
        return maxLength === 0 ? 1 : (maxLength - distance) / maxLength;
    }
    
    /**
     * Calculate Levenshtein distance between two strings
     * @param {string} str1 - First string
     * @param {string} str2 - Second string
     * @returns {number} Edit distance
     */
    levenshteinDistance(str1, str2) {
        const matrix = [];
        
        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }
        
        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }
        
        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1, // substitution
                        matrix[i][j - 1] + 1,     // insertion
                        matrix[i - 1][j] + 1      // deletion
                    );
                }
            }
        }
        
        return matrix[str2.length][str1.length];
    }
    
    /**
     * Check if text contains negation around a trigger phrase
     * @param {string} text - Full text
     * @param {string} phrase - Trigger phrase
     * @returns {boolean} True if negated
     */
    hasNegation(text, phrase) {
        const phraseIndex = text.indexOf(phrase);
        if (phraseIndex === -1) return false;
        
        // Get context around the phrase (more precise token-based approach)
        const tokens = text.split(' ');
        const phraseTokens = phrase.split(' ');
        
        // Find phrase token index
        let phraseTokenIndex = -1;
        for (let i = 0; i <= tokens.length - phraseTokens.length; i++) {
            if (tokens.slice(i, i + phraseTokens.length).join(' ') === phrase) {
                phraseTokenIndex = i;
                break;
            }
        }
        
        if (phraseTokenIndex === -1) return false;
        
        // Get context tokens around the phrase
        const contextStart = Math.max(0, phraseTokenIndex - this.options.negationWindow);
        const contextEnd = Math.min(tokens.length, phraseTokenIndex + phraseTokens.length + this.options.negationWindow);
        const contextTokens = tokens.slice(contextStart, contextEnd);
        
        // Check for negation words in context
        const hasNegation = this.options.negationWords.some(negation => 
            contextTokens.some(token => 
                token.toLowerCase().includes(negation.toLowerCase())
            )
        );
        
        if (hasNegation) {
            console.log(`🎯 TriggerDetector: Negation detected in context: "${contextTokens.join(' ')}"`);
        }
        
        return hasNegation;
    }
    
    /**
     * Emit trigger detected event
     * @param {string} phrase - Detected phrase
     * @param {number} score - Similarity score
     * @param {string} fullText - Full text that contained the trigger
     */
    emitTriggerDetected(phrase, score, fullText) {
        if (this.onTriggerDetected) {
            this.onTriggerDetected({
                phrase,
                score,
                fullText,
                timestamp: Date.now(),
                consecutiveMatches: this.consecutiveMatches
            });
        }
    }
    
    /**
     * Emit trigger rejected event
     * @param {string} phrase - Rejected phrase
     * @param {number} score - Similarity score
     * @param {string} reason - Rejection reason
     */
    emitTriggerRejected(phrase, score, reason) {
        if (this.onTriggerRejected) {
            this.onTriggerRejected({
                phrase,
                score,
                reason,
                timestamp: Date.now()
            });
        }
    }
    
    /**
     * Set trigger detected callback
     * @param {Function} callback - Callback function
     */
    setOnTriggerDetected(callback) {
        this.onTriggerDetected = callback;
    }
    
    /**
     * Set trigger rejected callback
     * @param {Function} callback - Callback function
     */
    setOnTriggerRejected(callback) {
        this.onTriggerRejected = callback;
    }
    
    /**
     * Reset the detector state
     */
    reset() {
        this.tokenWindow = [];
        this.lastTriggerTime = 0;
        this.consecutiveMatches = 0;
        this.lastMatchScore = 0;
        this.lastMatchedPhrase = '';
        console.log('🎯 TriggerDetector: Reset');
    }
    
    /**
     * Get current state for debugging
     * @returns {Object} Current state
     */
    getState() {
        return {
            tokenWindow: this.tokenWindow.map(item => item.token).join(' '),
            windowSize: this.tokenWindow.length,
            lastTriggerTime: this.lastTriggerTime,
            consecutiveMatches: this.consecutiveMatches,
            lastMatchScore: this.lastMatchScore,
            lastMatchedPhrase: this.lastMatchedPhrase,
            inRefractoryPeriod: Date.now() - this.lastTriggerTime < this.options.refractoryPeriod
        };
    }
}
